import { useMemo, useState } from "react";
import styles from "../../ConversionAndPdpViewer.module.css";
import {
  formatCurrency,
  formatCurrencyPrecise,
  formatDelta,
  formatFractionPct,
  formatNumber,
  valueToneClass,
} from "./formatters";
import { RankingTabs } from "./ui";

function getInventoryStatusLabel(row) {
  const status = row.inventoryStatus || row.inventory_status;
  if (status === "out_of_stock") return "Out of stock";
  if (status === "low_stock") return "Low stock";
  if (status === "in_stock") return "In stock";
  return "";
}

function getFirstSkuLabel(row) {
  const variants = Array.isArray(row.variants) ? row.variants : [];
  const firstVariant = variants.find((variant) => variant?.sku || variant?.title);
  return firstVariant?.sku || firstVariant?.title || "";
}

function getActiveProductSortValue(row, key) {
  if (key === "product") {
    return row.title || row.itemName || row.name || "";
  }
  if (key === "totalInventory") {
    return Math.max(Number(row.totalInventory ?? row.total_inventory ?? 0), 0);
  }
  if (key === "skuInventory") {
    return getFirstSkuLabel(row);
  }
  if (key === "stockStatus") {
    return getInventoryStatusLabel(row);
  }
  if (key === "price") {
    return Number(row.unitPrice ?? row.price ?? row.commercialOutcome?.unitPrice ?? 0);
  }
  if (key === "pageViews") {
    return Number(row.pageViews ?? row.page_views ?? row.pdpViews ?? row.waterfall?.pdpViews ?? row.itemViewEvents ?? 0);
  }
  if (key === "addToCart") {
    return Number(row.addToCart ?? row.add_to_cart ?? row.waterfall?.addedToCart ?? row.itemsAddedToCart ?? 0);
  }
  if (key === "purchases") {
    return Number(row.ga4Purchases ?? row.purchases ?? row.ga4ItemsPurchased ?? row.itemsPurchased ?? 0);
  }
  if (key === "revenue") {
    return Number(
      row.commercialOutcome?.totalRevenueWithinWindow ??
        row.totalRevenueWithinWindow ??
        row.commercialOutcome?.revenue ??
        row.itemRevenue ??
        0,
    );
  }
  if (key === "contribution") {
    return Number(
      row.commercialOutcome?.contributionBeforeMarketing ??
        row.contributionBeforeMarketing ??
        row.commercialOutcome?.contribution ??
        row.contribution ??
        Number.NEGATIVE_INFINITY,
    );
  }
  if (key === "potentialContributionBeforeMarketing") {
    return Number(
      row.commercialOutcome?.potentialContributionBeforeMarketing ??
        row.potentialContributionBeforeMarketing ??
        row.potential_contribution_before_marketing ??
        Number.NEGATIVE_INFINITY,
    );
  }
  if (key === "contributionBeforeMarketingWithSales") {
    return Number(
      row.commercialOutcome?.contributionBeforeMarketingWithSales ??
        row.contributionBeforeMarketingWithSales ??
        row.contribution_before_marketing_with_sales ??
        Number.NEGATIVE_INFINITY,
    );
  }
  if (key === "contributionAfterMarketing") {
    return Number(
      row.commercialOutcome?.contributionAfterMarketing ??
        row.contributionAfterMarketing ??
        row.contribution_after_marketing ??
        Number.NEGATIVE_INFINITY,
    );
  }
  if (key === "finalProfitability") {
    return Number(
      row.commercialOutcome?.finalProfitability ??
        row.finalProfitability ??
        row.final_profitability ??
        Number.NEGATIVE_INFINITY,
    );
  }
  if (key === "impressions") {
    return Number(row.impressions ?? row.gscData?.impressions ?? 0);
  }
  if (key === "ctr") {
    return Number(row.ctr_pct ?? row.gscData?.ctr_pct ?? 0);
  }
  if (key === "checkout") {
    return Number(row.checkout ?? row.checkouts ?? row.itemsCheckedOut ?? row.waterfall?.checkedOut ?? 0);
  }
  return row.title || row.itemName || row.name || "";
}

function formatInventoryCount(value) {
  return formatNumber(Math.max(Number(value ?? 0), 0));
}

const ACTIVE_PRODUCT_VIEW_OPTIONS = [
  { key: "overview", label: "Overview" },
  { key: "sales", label: "Sales" },
  { key: "margins", label: "Margins" },
  { key: "conversions", label: "Conversions" },
  { key: "demand", label: "Demand" },
];

const ACTIVE_PRODUCT_COLUMN_KEYS = {
  overview: [
    "totalInventory",
    "pageViews",
    "addToCart",
    "purchases",
    "price",
    "revenue",
    "contribution",
  ],
  sales: ["price", "revenue"],
  margins: [
    "potentialContributionBeforeMarketing",
    "contributionBeforeMarketingWithSales",
    "contributionAfterMarketing",
    "finalProfitability",
  ],
  conversions: ["pageViews", "addToCart", "checkout", "purchases"],
  demand: ["impressions", "ctr", "pageViews", "addToCart", "checkout", "purchases"],
};

export default function PdpRankingsTab({
  data,
  activeRankingTab,
  onRankingTabChange,
  onOpenProduct,
  currency = "USD",
  gscEnrichment = {},
  loadingRankings = false,
  rankingsError = "",
  loadingActiveProducts = false,
  activeProductsError = "",
}) {
  const [activeProductQuery, setActiveProductQuery] = useState("");
  const [activeProductSort, setActiveProductSort] = useState(null);
  const [activeProductView, setActiveProductView] = useState("overview");
  const rawRows = data?.[activeRankingTab] || [];
  const baseRows = activeRankingTab === "bestConverting"
    ? rawRows.filter((r) => (r.conversionQuality?.pdpToPurchaseRate ?? r.purchaseToViewRate ?? 0) > 0 && (r.commercialOutcome?.revenue ?? r.itemRevenue ?? 0) > 0)
    : rawRows;
  const activeRows = useMemo(() => {
    if (activeRankingTab !== "activeProducts") return baseRows;
    const query = activeProductQuery.trim().toLowerCase();
    const filteredRows = query ? baseRows.filter((row) => {
      const variants = Array.isArray(row.variants) ? row.variants : [];
      const searchableText = [
        row.title,
        row.itemName,
        row.name,
        row.handle,
        row.shopifyProductId,
        row.inventoryStatus,
        row.inventory_status,
        ...variants.flatMap((variant) => [
          variant.sku,
          variant.title,
          variant.shopify_variant_id,
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return searchableText.includes(query);
    }) : baseRows;

    if (!activeProductSort?.key) return filteredRows;
    const direction = activeProductSort.direction === "desc" ? -1 : 1;
    return [...filteredRows].sort((a, b) => {
      const aValue = getActiveProductSortValue(a, activeProductSort.key);
      const bValue = getActiveProductSortValue(b, activeProductSort.key);
      if (activeProductSort.key === "totalInventory") {
        return (aValue - bValue) * direction;
      }
      if ([
        "price",
        "revenue",
        "contribution",
        "pageViews",
        "addToCart",
        "purchases",
        "potentialContributionBeforeMarketing",
        "contributionBeforeMarketingWithSales",
        "contributionAfterMarketing",
        "finalProfitability",
        "impressions",
        "ctr",
        "checkout",
      ].includes(activeProductSort.key)) {
        return (aValue - bValue) * direction;
      }
      return String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: "base",
      }) * direction;
    });
  }, [activeRankingTab, activeProductQuery, activeProductSort, baseRows]);
  const activeProductColumnKeys =
    ACTIVE_PRODUCT_COLUMN_KEYS[activeProductView] || ACTIVE_PRODUCT_COLUMN_KEYS.overview;
  const emptyColSpan =
    activeRankingTab === "mostOpportunity"
      ? 3
      : activeRankingTab === "bestConverting"
        ? 4
          : activeRankingTab === "losingMomentum"
            ? 6
            : activeRankingTab === "activeProducts"
              ? activeProductColumnKeys.length + 1
            : activeRankingTab === "topSelling"
            ? 6
            : 5;
  const renderContributionCell = (row) => {
    const tooltip = "contribution before marketing";
    if (row.commercialOutcome?.contribution != null) {
      return (
        <span title={tooltip}>
          {formatCurrency(row.commercialOutcome.contribution, currency)}
        </span>
      );
    }
    if (row.contribution != null) {
      return (
        <span title={tooltip}>
          {formatCurrency(row.contribution, currency)}
        </span>
      );
    }
    if (row.commercialOutcome?.contributionStatus === "missing_cost_data") {
      return "Cost data missing";
    }
    return "—";
  };
  const renderMarginCell = (row) => {
    const contributionMissing =
      row.commercialOutcome?.contributionStatus === "missing_cost_data" ||
      ((row.commercialOutcome?.contribution ?? row.contribution) == null);
    if (contributionMissing) return "—";
    if (row.commercialOutcome?.marginPct != null) {
      return `${(row.commercialOutcome.marginPct * 100).toFixed(1)}%`;
    }
    if (row.marginPct != null) {
      return `${(row.marginPct * 100).toFixed(1)}%`;
    }
    return "—";
  };
  const renderInventoryStatus = (row) => {
    return getInventoryStatusLabel(row) || "—";
  };
  const renderSkuInventory = (row) => {
    const variants = Array.isArray(row.variants) ? row.variants : [];
    if (!variants.length) return "No SKU inventory";
    const visibleVariants = variants.slice(0, 3);
    return (
      <div style={{ display: "grid", gap: "0.25rem" }}>
        {visibleVariants.map((variant, index) => (
          <span key={`${variant.shopify_variant_id || variant.sku || index}`}>
            {variant.sku || variant.title || `Variant ${index + 1}`}:{" "}
            {formatInventoryCount(variant.inventory_quantity)}
          </span>
        ))}
        {variants.length > visibleVariants.length ? (
          <span>+{variants.length - visibleVariants.length} more SKUs</span>
        ) : null}
      </div>
    );
  };
  const toggleActiveProductSort = (key) => {
    setActiveProductSort((current) => {
      if (current?.key !== key) return { key, direction: "asc" };
      return { key, direction: current.direction === "asc" ? "desc" : "asc" };
    });
  };
  const renderSortButton = (key, label, alignRight = false) => {
    const isActive = activeProductSort?.key === key;
    const directionLabel = !isActive
      ? "Sort"
      : activeProductSort.direction === "asc"
        ? "Ascending"
        : "Descending";
    const indicator = !isActive
      ? "↕"
      : activeProductSort.direction === "asc"
        ? "↑"
        : "↓";
    return (
      <span className={`${styles.sortableHeaderContent} ${alignRight ? styles.sortableHeaderContentRight : ""}`}>
        <span>{label}</span>
        <button
          type="button"
          className={`${styles.sortHeaderButton} ${isActive ? styles.sortHeaderButtonActive : ""}`}
          onClick={() => toggleActiveProductSort(key)}
          title={`${directionLabel}: ${label}`}
          aria-label={`${directionLabel}: ${label}`}
        >
          {indicator}
        </button>
      </span>
    );
  };
  const getActiveProductColumnLabel = (key) => {
    const labels = {
      totalInventory: "Total inventory",
      pageViews: "Page views",
      addToCart: "Add to cart",
      purchases: "Purchases",
      price: "Price",
      revenue: "Revenue",
      contribution: "Contribution before marketing",
      potentialContributionBeforeMarketing: "Potential contribution before marketing (no sales)",
      contributionBeforeMarketingWithSales: "Contribution before marketing (with sales)",
      contributionAfterMarketing: "Contribution after marketing",
      finalProfitability: "Final profitability after overhead",
      impressions: "Impressions",
      ctr: "CTR",
      checkout: "Checkout",
    };
    return labels[key] || key;
  };
  const renderActiveProductCell = (row, key) => {
    if (key === "totalInventory") {
      return formatInventoryCount(row.totalInventory ?? row.total_inventory);
    }
    if (key === "pageViews") {
      return formatNumber(row.pageViews ?? row.page_views ?? row.pdpViews ?? row.waterfall?.pdpViews ?? row.itemViewEvents ?? 0);
    }
    if (key === "addToCart") {
      return formatNumber(row.addToCart ?? row.add_to_cart ?? row.waterfall?.addedToCart ?? row.itemsAddedToCart ?? 0);
    }
    if (key === "purchases") {
      return formatNumber(row.ga4Purchases ?? row.purchases ?? row.ga4ItemsPurchased ?? row.itemsPurchased ?? 0);
    }
    if (key === "price") {
      return row.unitPrice != null || row.price != null || row.commercialOutcome?.unitPrice != null
        ? formatCurrency(row.unitPrice ?? row.price ?? row.commercialOutcome?.unitPrice, currency)
        : "—";
    }
    if (key === "revenue") {
      return formatCurrency(
        row.commercialOutcome?.totalRevenueWithinWindow ??
          row.totalRevenueWithinWindow ??
          row.commercialOutcome?.revenue ??
          row.itemRevenue ??
          0,
        currency,
      );
    }
    if (key === "contribution") {
      return renderContributionCell(row);
    }
    if (key === "potentialContributionBeforeMarketing") {
      return formatCurrency(
        row.commercialOutcome?.potentialContributionBeforeMarketing ??
          row.potentialContributionBeforeMarketing ??
          row.potential_contribution_before_marketing ??
          0,
        currency,
      );
    }
    if (key === "contributionBeforeMarketingWithSales") {
      return formatCurrency(
        row.commercialOutcome?.contributionBeforeMarketingWithSales ??
          row.contributionBeforeMarketingWithSales ??
          row.contribution_before_marketing_with_sales ??
          0,
        currency,
      );
    }
    if (key === "contributionAfterMarketing") {
      return formatCurrency(
        row.commercialOutcome?.contributionAfterMarketing ??
          row.contributionAfterMarketing ??
          row.contribution_after_marketing ??
          0,
        currency,
      );
    }
    if (key === "finalProfitability") {
      return formatCurrency(
        row.commercialOutcome?.finalProfitability ??
          row.finalProfitability ??
          row.final_profitability ??
          0,
        currency,
      );
    }
    if (key === "impressions") {
      return row.impressions != null || row.gscData?.impressions != null
        ? formatNumber(row.impressions ?? row.gscData?.impressions)
        : "—";
    }
    if (key === "ctr") {
      const ctrPct = row.ctr_pct ?? row.gscData?.ctr_pct;
      if (ctrPct != null) return `${Number(ctrPct).toFixed(1)}%`;
      if (row.ctr != null) return `${(Number(row.ctr) * 100).toFixed(1)}%`;
      return "—";
    }
    if (key === "checkout") {
      return formatNumber(row.checkout ?? row.checkouts ?? row.itemsCheckedOut ?? row.waterfall?.checkedOut ?? 0);
    }
    return "—";
  };
  return (
    <div className={styles.sectionStack}>
      <RankingTabs activeTab={activeRankingTab} onChange={onRankingTabChange} />

      {/* {data?.aiOverview ? (
        <p className={styles.descriptionText}>{data.aiOverview}</p>
      ) : null} */}

      {activeRankingTab === "activeProducts" ? (
        <div className={styles.rankingSearchRow}>
          <input
            className={styles.rankingSearchInput}
            type="search"
            value={activeProductQuery}
            onChange={(event) => setActiveProductQuery(event.target.value)}
            placeholder="Search active products or SKUs"
            aria-label="Search active products or SKUs"
          />
          <select
            className={styles.rankingMetricSelect}
            value={activeProductView}
            onChange={(event) => {
              setActiveProductView(event.target.value);
              setActiveProductSort(null);
            }}
            aria-label="Active products metric group"
          >
            {ACTIVE_PRODUCT_VIEW_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            {activeRankingTab === "topSelling" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>Revenue</th>
                <th className={styles.alignRight}>Items sold</th>
                <th className={styles.alignRight}>PDP views</th>
                <th className={styles.alignRight}>Conv.</th>
                <th className={styles.alignRight}>
                  <span title="contribution before marketing">Contribution</span>
                </th>
              </tr>
            )}
            {activeRankingTab === "highestContribution" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>Contribution</th>
                <th className={styles.alignRight}>Margin %</th>
                <th className={styles.alignRight}>Revenue</th>
                <th className={styles.alignRight}>Items sold</th>
              </tr>
            )}
            {activeRankingTab === "mostOpportunity" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>PDP views</th>
                <th className={styles.alignRight}>ATC rate</th>
              </tr>
            )}
            {activeRankingTab === "bestConverting" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>PDP to purchase</th>
                <th className={styles.alignRight}>Revenue/session</th>
                <th className={styles.alignRight}>Contrib./session</th>
              </tr>
            )}
            {activeRankingTab === "losingMomentum" && (
              <tr>
                <th>Product</th>
                <th className={styles.alignRight}>Impressions</th>
                <th className={styles.alignRight}>Clicks</th>
                <th className={styles.alignRight}>CTR</th>
                <th className={styles.alignRight}>Position</th>
                <th>Signal</th>
              </tr>
            )}
            {activeRankingTab === "activeProducts" && (
              <tr>
                <th>{renderSortButton("product", "Product")}</th>
                {activeProductColumnKeys.map((key) => (
                  <th key={key} className={styles.alignRight}>
                    {renderSortButton(key, getActiveProductColumnLabel(key), true)}
                  </th>
                ))}
                {/* <th>{renderSortButton("skuInventory", "SKU inventory")}</th> */}
                {/* <th>{renderSortButton("stockStatus", "Stock status")}</th> */}
              </tr>
            )}
          </thead>
          <tbody>
            {activeRows.length === 0 ? (
              <tr>
                <td colSpan={emptyColSpan} style={{ textAlign: "center", color: "var(--tm)", padding: "1.5rem" }}>
                  {activeRankingTab === "activeProducts" && loadingActiveProducts
                    ? "Loading active products..."
                    : activeRankingTab === "activeProducts" && activeProductsError
                      ? "Unable to load active products right now."
                      : activeRankingTab !== "activeProducts" && loadingRankings
                        ? "Loading PDP rankings..."
                        : activeRankingTab !== "activeProducts" && rankingsError
                          ? "Unable to load PDP rankings right now."
                      : "No data available for this ranking."}
                </td>
              </tr>
            ) : (
              activeRows.map((row, index) => {
                const productId = String(row.shopifyProductId || row.productId || row.itemId || row.id || "");
                return (
                  <tr
                    key={productId || index}
                    className={styles.clickableRow}
                    onClick={() => onOpenProduct(row)}
                  >
                    <td>{row.title || row.itemName || row.name || "—"}</td>

                    {activeRankingTab === "topSelling" && (
                      <>
                        <td className={styles.alignRight}>{formatCurrency(row.commercialOutcome?.revenue ?? row.itemRevenue, currency)}</td>
                        <td className={styles.alignRight}>{formatNumber(row.commercialOutcome?.unitsSold ?? row.itemsCheckedOut)}</td>
                        <td className={styles.alignRight}>{formatNumber(row.waterfall?.pdpViews ?? row.itemViewEvents)}</td>
                        <td className={styles.alignRight}>
                          {row.conversionQuality?.bundleBehavior?.detected
                            ? <span title={row.conversionQuality.bundleBehavior.note || "Bundle/cross-sell purchase — no PDP visit"}>Bundle *</span>
                            : formatFractionPct(row.conversionQuality?.pdpToPurchaseRate ?? row.purchaseToViewRate)
                          }
                        </td>
                        <td className={styles.alignRight}>{renderContributionCell(row)}</td>
                      </>
                    )}

                    {activeRankingTab === "highestContribution" && (
                      <>
                        <td className={styles.alignRight}>{renderContributionCell(row)}</td>
                        <td className={styles.alignRight}>{renderMarginCell(row)}</td>
                        <td className={styles.alignRight}>{formatCurrency(row.commercialOutcome?.revenue ?? row.itemRevenue, currency)}</td>
                        <td className={styles.alignRight}>{formatNumber(row.commercialOutcome?.unitsSold ?? row.itemsCheckedOut)}</td>
                      </>
                    )}

                    {activeRankingTab === "mostOpportunity" && (
                      <>
                        <td className={styles.alignRight}>{formatNumber(row.waterfall?.pdpViews ?? row.itemViewEvents)}</td>
                        <td className={styles.alignRight}>{formatFractionPct(row.conversionQuality?.pdpToAtcRate ?? row.cartToViewRate)}</td>
                      </>
                    )}

                    {activeRankingTab === "bestConverting" && (
                      <>
                        <td className={styles.alignRight}>{formatFractionPct(row.conversionQuality?.pdpToPurchaseRate ?? row.purchaseToViewRate)}</td>
                        <td className={styles.alignRight}>{formatCurrencyPrecise(row.commercialOutcome?.revenuePerSession ?? row.revenuePerView, currency)}</td>
                        <td className={styles.alignRight}>{row.commercialOutcome?.contributionPerSession != null ? formatCurrencyPrecise(row.commercialOutcome.contributionPerSession, currency) : row.contributionPerView != null ? formatCurrencyPrecise(row.contributionPerView, currency) : "—"}</td>
                      </>
                    )}

                    {activeRankingTab === "losingMomentum" && (() => {
                      const gsc = row.gscData || gscEnrichment[row.shopifyProductId] || null;
                      return (
                        <>
                          <td className={styles.alignRight}>
                            {gsc?.impressions != null ? formatNumber(gsc.impressions) : "—"}
                          </td>
                          <td className={styles.alignRight}>
                            {gsc?.clicks != null ? formatNumber(gsc.clicks) : "—"}
                          </td>
                          <td className={styles.alignRight}>
                            {gsc?.ctr_pct != null ? `${Number(gsc.ctr_pct).toFixed(1)}%` : "—"}
                          </td>
                          <td className={styles.alignRight}>
                            {gsc?.position != null ? Number(gsc.position).toFixed(1) : "—"}
                          </td>
                          <td>{row.signalLabel || "Monitor"}</td>
                        </>
                      );
                    })()}

                    {activeRankingTab === "activeProducts" && (
                      <>
                        {activeProductColumnKeys.map((key) => (
                          <td key={key} className={styles.alignRight}>
                            {renderActiveProductCell(row, key)}
                          </td>
                        ))}
                        {/* <td>{renderSkuInventory(row)}</td> */}
                        {/* <td>{renderInventoryStatus(row)}</td> */}
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
